import React, { useEffect, useState } from 'react';
import { supabase } from '../supabaseClient';
import { UserProfile } from '../types';
import { Avatar } from './Avatar';
import { Trophy, Medal, Crown } from 'lucide-react';
import { getTierDetails, getTier, trackFeatureUsage, addXpToProfile } from '../services/gamificationService';
import { LayoutGrid, Users, ShoppingBag, Car, Calendar, Globe, EyeOff, Search, UserPlus, X, Loader2 } from 'lucide-react';

const VIP_EMAILS = [
    'tiagoromao25@gmail.com',
    'anapaulaautentica@gmail.com',
    'eenokibara@gmail.com',
    'josesantostimes@gmail.com',
    'ana.ubersp@gmail.com',
    'nelsandromatias@hotmail.com',
    'mmmontefuscobernardo@gmail.com',
    'mmontefuscobernardo@gmail.com'
];

const isVipEmail = (email: string | undefined): boolean => {
    if (!email) return false;
    const lowerEmail = email.toLowerCase();
    if (lowerEmail.startsWith('eenokibara@')) return true;
    return VIP_EMAILS.includes(lowerEmail);
};

interface RankingProps {
    currentUserId?: string;
    isPrivacyMode?: boolean;
    onViewProfile?: (userId: string) => void;
}

export const RankingView: React.FC<RankingProps> = ({ currentUserId, isPrivacyMode = false, onViewProfile }) => {
    const [profiles, setProfiles] = useState<UserProfile[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [timeFilter, setTimeFilter] = useState<'weekly' | 'monthly' | 'alltime'>('alltime'); // Changed default to alltime for initial show
    const [categoryFilter, setCategoryFilter] = useState<string>('ALL');

    // Add Friend States
    const [isAddFriendModalOpen, setIsAddFriendModalOpen] = useState(false);
    const [friendSearchTerm, setFriendSearchTerm] = useState('');
    const [friendSearchResults, setFriendSearchResults] = useState<UserProfile[]>([]);
    const [isSearchingFriend, setIsSearchingFriend] = useState(false);

    useEffect(() => {
        fetchRanking();
    }, [timeFilter, categoryFilter]);

    const fetchRanking = async () => {
        setIsLoading(true);
        try {
            // If "Amigos" filter is active, show only tracked friends
            if (categoryFilter === 'REFERRAL') {
                if (!currentUserId) {
                    setProfiles([]);
                    setIsLoading(false);
                    return;
                }

                // 1. Fetch the current user's tracked_friends list
                const { data: myProfile, error: myErr } = await supabase
                    .from('profiles')
                    .select('tracked_friends')
                    .eq('id', currentUserId)
                    .single();

                if (myErr || !myProfile?.tracked_friends || myProfile.tracked_friends.length === 0) {
                    setProfiles([]);
                    setIsLoading(false);
                    return;
                }

                // 2. Fetch only those friend profiles
                const { data: friendProfiles, error: friendErr } = await supabase
                    .from('profiles')
                    .select('*')
                    .in('id', myProfile.tracked_friends)
                    .order('xp_points', { ascending: false });

                if (friendErr) {
                    console.error('Error fetching friend profiles:', friendErr);
                    setProfiles([]);
                } else {
                    setProfiles(friendProfiles || []);
                }

                setIsLoading(false);
                return;
            }

            // Normal ranking flow for other filters
            const { data, error } = await supabase.rpc('get_ranking', {
                p_time_filter: timeFilter,
                p_category_filter: categoryFilter
            });

            if (error) {
                console.error('Error fetching ranking via RPC:', error);
                // Fallback to old simple query if RPC fails (e.g. not created yet)
                if (timeFilter === 'alltime' && categoryFilter === 'ALL') {
                     const { data: fallbackData } = await supabase
                        .from('profiles')
                        .select('*')
                        .order('xp_points', { ascending: false })
                        .limit(100);
                     setProfiles(fallbackData || []);
                }
            } else if (data && data.length > 0) {
                // get_ranking RPC now returns all needed fields directly (avatar_url, privacy settings)
                setProfiles(data);
            } else {
                setProfiles([]);
            }
        } catch (err) {
            console.error('Error in fetchRanking:', err);
        } finally {
            setIsLoading(false);
        }
    };

    const handleSearchFriend = async () => {
        if (!friendSearchTerm || friendSearchTerm.length < 3) return;
        setIsSearchingFriend(true);
        try {
            const { data, error } = await supabase
                .from('profiles')
                .select('*')
                .ilike('nickname', `%${friendSearchTerm}%`)
                .neq('id', currentUserId)
                .limit(10);
            
            if (error) throw error;
            setFriendSearchResults(data || []);
        } catch (err) {
            console.error('Error searching friends:', err);
        } finally {
            setIsSearchingFriend(false);
        }
    };

    const handleAddFriend = async (friendId: string) => {
        if (!currentUserId) return;
        try {
            // First fetch current friends array
            const { data: userProfile, error: fetchErr } = await supabase
                .from('profiles')
                .select('tracked_friends')
                .eq('id', currentUserId)
                .single();
            if (fetchErr) throw fetchErr;

            let currentFriends: string[] = userProfile?.tracked_friends || [];
            if (!currentFriends.includes(friendId)) {
                currentFriends.push(friendId);
                const { error: updateErr } = await supabase
                    .from('profiles')
                    .update({ tracked_friends: currentFriends })
                    .eq('id', currentUserId);
                if (updateErr) throw updateErr;
                
                alert('Amigo adicionado com sucesso! Atualizando ranking...');
                setIsAddFriendModalOpen(false);
                setFriendSearchTerm('');
                setFriendSearchResults([]);
                fetchRanking();
            } else {
                alert('Você já acompanha este motorista!');
            }
        } catch (err) {
            console.error('Error adding friend tracking:', err);
            alert('Erro ao adicionar. Tente novamente.');
        }
    };

    const TopPodium = ({ profile, position }: { profile: UserProfile, position: number }) => {
        const tier = getTier(profile.xp_points || 0);
        const details = getTierDetails(tier);

        let scale = 'scale-100';
        let color = 'text-slate-400';
        let icon = <Medal className="w-8 h-8" />;

        if (position === 1) {
            scale = 'scale-110 -mt-4';
            color = 'text-yellow-400 drop-shadow-md';
            icon = <Crown className="w-10 h-10 text-yellow-500 fill-yellow-500/20 animate-pulse" />;
        } else if (position === 2) {
            color = 'text-slate-300 drop-shadow-md';
            icon = <Medal className="w-8 h-8 text-slate-300 fill-slate-300/20" />;
        } else if (position === 3) {
            scale = 'scale-95 mt-4';
            color = 'text-amber-700 drop-shadow-md';
            icon = <Medal className="w-8 h-8 text-amber-700 fill-amber-700/20" />;
        }

        return (
            <div 
                onClick={() => onViewProfile && onViewProfile(profile.id)}
                className={`flex flex-col items-center mx-2 ${scale} transition-all cursor-pointer hover:scale-105 active:scale-95`}
            >
                <div className={`relative mb-2 ${color} animate-bounce-slow`}>
                    {icon}
                </div>
                <div className={`relative p-1 rounded-full bg-gradient-to-br ${details.gradient} shadow-xl`}>
                    <Avatar 
                        url={profile.privacy_hide_avatar ? '' : profile.avatar_url} 
                        size={position === 1 ? 'lg' : 'md'} 
                        isPremium={profile.is_premium}
                        isVip={isVipEmail(profile.email)}
                        showLabel={true}
                        xp={profile.xp_points || 0}
                    />
                    <div className={`absolute -top-2 -right-2 text-white text-[10px] w-6 h-6 flex items-center justify-center rounded-full font-bold border border-white/20 shadow-lg z-30 ${position === 1 ? 'bg-yellow-600' : position === 2 ? 'bg-slate-500' : 'bg-amber-800'
                        }`}>
                        {position}º
                    </div>
                </div>
                <div className="mt-3 text-center">
                    <p className="font-bold text-sm text-white truncate max-w-[80px]">
                        {profile.nickname || 'Motorista'}
                    </p>
                    <p className="text-[10px] text-slate-400 font-mono">
                        {isPrivacyMode || profile.privacy_hide_stats ? '**** XP' : `${profile.xp_points} XP`}
                    </p>
                    <p className={`text-[9px] uppercase font-bold mt-1 ${details.color}`}>
                        {profile.privacy_hide_stats ? 'Piloto Oculto' : details.label}
                    </p>
                </div>
            </div>
        );
    };

    return (
        <div id="tour-ranking" className="space-y-6 pb-20 animate-fade-in px-1">
            <div className="text-center mb-6">
                <h2 className="text-2xl font-bold text-white flex items-center justify-center gap-2">
                    <Trophy className="w-6 h-6 text-yellow-500" /> Ranking de Pilotos
                </h2>
                <div className="mt-2 inline-flex items-center gap-2 bg-blue-500/10 border border-blue-500/20 px-3 py-1 rounded-full">
                    <span className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></span>
                    <p className="text-[10px] font-bold text-blue-400 uppercase tracking-wider">
                        Resets automáticos ativos ⚡
                    </p>
                </div>
            </div>

            {/* Filter Controls */}
            <div className="space-y-4">
                {/* Time Switch */}
                <div className="flex bg-slate-900/50 p-1 rounded-xl border border-slate-700/50 max-w-sm mx-auto">
                    <button 
                        onClick={() => setTimeFilter('weekly')}
                        className={`flex-1 flex items-center justify-center gap-1 py-2 rounded-lg text-xs font-bold transition-all ${timeFilter === 'weekly' ? 'bg-blue-600 text-white shadow-lg' : 'text-slate-500 hover:text-slate-300'}`}
                    >
                        <Calendar size={14} className="hidden sm:block" /> SEMANAL
                    </button>
                    <button 
                        onClick={() => setTimeFilter('monthly')}
                        className={`flex-1 flex items-center justify-center gap-1 py-2 rounded-lg text-xs font-bold transition-all ${timeFilter === 'monthly' ? 'bg-blue-600 text-white shadow-lg' : 'text-slate-500 hover:text-slate-300'}`}
                    >
                        <Calendar size={14} className="hidden sm:block" /> MENSAL
                    </button>
                    <button 
                        onClick={() => setTimeFilter('alltime')}
                        className={`flex-1 flex items-center justify-center gap-1 py-2 rounded-lg text-xs font-bold transition-all ${timeFilter === 'alltime' ? 'bg-blue-600 text-white shadow-lg' : 'text-slate-500 hover:text-slate-300'}`}
                    >
                        <Globe size={14} className="hidden sm:block" /> GERAL
                    </button>
                </div>

                {/* Category Chips */}
                <div className="flex overflow-x-auto pb-2 gap-2 scrollbar-none snap-x px-2">
                    {[
                        { id: 'ALL', label: 'Tudo', icon: <LayoutGrid size={14} /> },
                        { id: 'SHIFT', label: 'Turnos', icon: <Car size={14} /> },
                        { id: 'REFERRAL', label: 'Amigos', icon: <Users size={14} /> },
                        { id: 'STORY', label: 'Vendas', icon: <ShoppingBag size={14} /> }
                    ].map(cat => (
                        <button
                            key={cat.id}
                            onClick={() => setCategoryFilter(cat.id)}
                            className={`flex items-center gap-2 px-4 py-2 rounded-full whitespace-nowrap text-xs font-bold border transition-all snap-start ${
                                categoryFilter === cat.id 
                                ? 'bg-amber-500/10 border-amber-500 text-amber-500 shadow-[0_0_10px_rgba(245,158,11,0.2)]' 
                                : 'bg-slate-800 border-slate-700 text-slate-400 hover:border-slate-500'
                            }`}
                        >
                            {cat.icon} {cat.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* Add Friend Button when REFERRAL is selected */}
            {categoryFilter === 'REFERRAL' && (
                <div className="flex justify-center mt-2 mb-4 animate-fade-in">
                    <button 
                        onClick={() => setIsAddFriendModalOpen(true)}
                        className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white font-bold text-xs rounded-lg shadow-lg shadow-blue-500/20 transition-all active:scale-95"
                    >
                        <UserPlus size={16} /> ADICIONAR MOTORISTA PARA ACOMPANHAR
                    </button>
                </div>
            )}

            {/* Podium */}
            {profiles.length > 0 && (
                <div className="flex justify-center items-end py-8 mb-4">
                    {profiles[1] && <TopPodium profile={profiles[1]} position={2} />}
                    {profiles[0] && <TopPodium profile={profiles[0]} position={1} />}
                    {profiles[2] && <TopPodium profile={profiles[2]} position={3} />}
                </div>
            )}

            {/* List */}
            <div className="bg-slate-800/50 rounded-xl border border-slate-700 overflow-hidden">
                {profiles.slice(3).map((profile, idx) => {
                    const position = idx + 4;
                    const tier = getTier(profile.xp_points || 0);
                    const details = getTierDetails(tier);

                    return (
                        <div 
                            key={profile.id} 
                            onClick={() => onViewProfile && onViewProfile(profile.id)}
                            className="flex items-center p-4 border-b border-slate-700/50 last:border-0 hover:bg-slate-800 transition-colors cursor-pointer"
                        >
                            <span className="w-8 text-center text-slate-500 font-bold font-mono text-sm">{position}º</span>
                            <div className="ml-2 mr-4">
                                <Avatar 
                                    url={profile.privacy_hide_avatar ? '' : profile.avatar_url} 
                                    size="sm" 
                                    isPremium={profile.is_premium}
                                    isVip={isVipEmail(profile.email)}
                                    showLabel={true}
                                    xp={profile.xp_points || 0}
                                />
                            </div>
                            <div className="flex-1 overflow-hidden">
                                <p className="text-sm font-bold text-white truncate">
                                    {profile.nickname || 'Motorista'}
                                    {profile.id === currentUserId && <span className="ml-2 text-[10px] bg-blue-600 px-1.5 py-0.5 rounded text-white inline-block relative -top-0.5">Você</span>}
                                </p>
                                <p className="text-[10px] text-slate-400 truncate">
                                    {profile.privacy_hide_stats ? 'Piloto Oculto' : details.label} • {isPrivacyMode || profile.privacy_hide_stats ? '**** XP' : `${profile.xp_points} XP`}
                                </p>
                            </div>
                            <div className={`text-xs font-bold ${profile.privacy_hide_stats ? 'text-slate-600' : details.color}`}>
                                {profile.privacy_hide_stats ? <EyeOff size={16} /> : details.icon}
                            </div>
                        </div>
                    );
                })}

                {profiles.length === 0 && !isLoading && (
                    <div className="p-8 text-center text-slate-500 text-sm">
                        Nenhum motorista {categoryFilter === 'REFERRAL' ? 'sendo acompanhado.' : 'encontrado no ranking.'}
                    </div>
                )}
            </div>

            {/* Add Friend Modal */}
            {isAddFriendModalOpen && (
                <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4 animate-fade-in backdrop-blur-sm">
                    <div className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-md shadow-2xl overflow-hidden flex flex-col">
                        <div className="p-4 border-b border-slate-700 flex justify-between items-center bg-slate-800">
                            <h3 className="text-lg font-bold text-white flex items-center gap-2">
                                <Search className="w-5 h-5 text-blue-400" /> Buscar Motorista
                            </h3>
                            <button onClick={() => setIsAddFriendModalOpen(false)} className="text-slate-400 hover:text-white p-1">
                                <X className="w-6 h-6" />
                            </button>
                        </div>
                        <div className="p-4 flex-1">
                            <div className="flex gap-2 mb-4">
                                <input
                                    type="text"
                                    value={friendSearchTerm}
                                    onChange={(e) => setFriendSearchTerm(e.target.value)}
                                    placeholder="Digite o nome de usuário (Ex: TiagoRomao)"
                                    className="flex-1 bg-slate-800 border border-slate-700 text-white text-sm rounded-lg p-3 placeholder-slate-500 focus:outline-none focus:border-blue-500"
                                    onKeyDown={(e) => e.key === 'Enter' && handleSearchFriend()}
                                />
                                <button 
                                    onClick={handleSearchFriend}
                                    disabled={isSearchingFriend || friendSearchTerm.length < 3}
                                    className="bg-blue-600 hover:bg-blue-500 text-white p-3 rounded-lg flex items-center justify-center disabled:opacity-50"
                                >
                                    {isSearchingFriend ? <Loader2 className="w-5 h-5 animate-spin" /> : <Search className="w-5 h-5" />}
                                </button>
                            </div>
                            
                            <div className="max-h-[300px] overflow-y-auto space-y-2 pr-1 custom-scrollbar">
                                {friendSearchResults.length > 0 ? (
                                    friendSearchResults.map(fp => (
                                        <div key={fp.id} className="flex items-center justify-between p-3 bg-slate-800 rounded-xl border border-slate-700">
                                            <div className="flex items-center gap-3">
                                                <Avatar size="sm" url={fp.privacy_hide_avatar ? '' : fp.avatar_url} xp={fp.xp_points || 0} />
                                                <div>
                                                    <p className="text-sm font-bold text-white">{fp.nickname}</p>
                                                    <p className="text-[10px] text-slate-400 font-mono italic">{fp.privacy_hide_stats ? 'Piloto Oculto' : `${fp.xp_points || 0} XP`}</p>
                                                </div>
                                            </div>
                                            <button 
                                                onClick={() => handleAddFriend(fp.id)}
                                                className="bg-slate-700 hover:bg-slate-600 text-blue-400 px-3 py-1.5 rounded-lg text-xs font-bold transition-colors flex items-center gap-1"
                                            >
                                                <UserPlus className="w-3 h-3" /> Seguir
                                            </button>
                                        </div>
                                    ))
                                ) : (
                                    friendSearchTerm.length >= 3 && !isSearchingFriend && (
                                        <div className="text-center text-slate-500 text-sm py-4">Nenhum motorista encontrado.</div>
                                    )
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
